import type { Shape } from "@/client/utils/geometry";
import { px } from "@/client/utils/units";

export const createEl = <T_Key extends keyof HTMLElementTagNameMap>(
    tagName: T_Key,
    className: string,
    children: string | Node[] = "",
    init?: (el: HTMLElementTagNameMap[T_Key]) => void,
    options?: ElementCreationOptions,
): HTMLElementTagNameMap[T_Key] => {
    const el = document.createElement(tagName, options);
    el.className = className;

    if (typeof children === "string") {
        el.textContent = children;
    } else {
        el.replaceChildren(...children);
    }

    init?.(el);

    return el;
};

export interface Component<T extends HTMLElement> {
    el: T;
}

export const wrapCmp = <T extends HTMLElement, R extends Component<T>>(
    el: T,
    init: (el: T) => R,
): R => init(el);

export const createCmp = <
    T_Key extends keyof HTMLElementTagNameMap,
    T_Return extends Component<HTMLElementTagNameMap[T_Key]>,
>(
    tagName: T_Key,
    className: string,
    children: string | Node[],
    init: (el: HTMLElementTagNameMap[T_Key]) => T_Return,
    options?: ElementCreationOptions,
): T_Return => {
    const el = document.createElement(tagName, options);
    el.className = className;

    if (typeof children === "string") {
        el.textContent = children;
    } else {
        el.replaceChildren(...children);
    }

    return init(el);
};

export interface CanvasComponent {
    el: HTMLCanvasElement;
    ctx: CanvasRenderingContext2D;
    shape: Shape;
    resize: (width: number, height: number, widthStyle?: string, heightStyle?: string) => void;
}

export const resizeCanvas2D = (
    el: HTMLCanvasElement,
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    widthStyle?: string,
    heightStyle?: string,
) => {
    el.width = Math.round(width * window.devicePixelRatio);
    el.height = Math.round(height * window.devicePixelRatio);
    el.style.width = widthStyle ?? px(width);
    el.style.height = heightStyle ?? px(height);
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);
};

export const createCanvasCmp = <T extends object>(
    className: string,
    width_px: number,
    height_px: number,
    init?: (el: HTMLCanvasElement, ctx: CanvasRenderingContext2D) => T | undefined,
    widthStyle?: string,
    heightStyle?: string,
    settings?: CanvasRenderingContext2DSettings,
): CanvasComponent & T => {
    const el = document.createElement("canvas");
    el.className = className;

    const ctx = el.getContext("2d", settings);
    if (ctx === null) {
        throw new Error("Failed to get 2d context");
    }
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    const shape: Shape = { w: width_px, h: height_px };

    const resize = (
        newWidth_px: number,
        newHeight_px: number,
        newWidthStyle?: string,
        newHeightStyle?: string,
    ) => {
        shape.w = newWidth_px;
        shape.h = newHeight_px;
        resizeCanvas2D(el, ctx, newWidth_px, newHeight_px, newWidthStyle, newHeightStyle);
    };
    resize(width_px, height_px, widthStyle, heightStyle);

    const props: T = init?.(el, ctx) ?? ({} as T);

    return { el, ctx, shape, resize, ...props };
};
